import { useEffect, useState, type FormEvent } from 'react';
import { Button, Field, Input } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import type { MfaSetup } from '@/features/auth/types';
import { isApiError } from '@/lib/api';
import { RecoveryCodes, TotpQr } from '@/layouts/shell/UserMenu';
import styles from './SignInPage.module.css';

/**
 * Second sign-in step (STF-01). The API decides whether the active account
 * requires two-factor (including invited locums): first sign-in sets it up,
 * then later sign-ins accept an app or recovery code.
 */
export function MfaStep({ setupRequired }: { setupRequired: boolean }) {
  const { startMfaSetup, verifyMfa, cancelMfa } = useSession();
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!setupRequired) return;
    let live = true;
    startMfaSetup()
      .then((s) => live && setSetup(s))
      .catch(
        (err: unknown) =>
          live &&
          setError(
            isApiError(err) ? err.message : 'Could not start two-factor setup.',
          ),
      );
    return () => {
      live = false;
    };
  }, [setupRequired, startMfaSetup]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifyMfa(
        useRecovery ? { recoveryCode: code.trim() } : { code: code.trim() },
      );
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Code not accepted');
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  const ready = useRecovery ? code.trim().length >= 12 : /^\d{6}$/.test(code);

  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      <h2 className={styles.title}>
        {setupRequired ? 'Set up two-factor' : 'Two-factor'}
      </h2>
      <p className={styles.subtitle}>
        {setupRequired
          ? 'Your role requires two-factor. Scan this with an authenticator app, then enter the 6-digit code it shows.'
          : useRecovery
            ? 'Enter one of your recovery codes.'
            : 'Enter the 6-digit code from your authenticator app.'}
      </p>
      {setupRequired && setup && <TotpQr setup={setup} />}
      <Field label={useRecovery ? 'Recovery code' : 'Code'}>
        <Input
          autoFocus
          autoComplete="one-time-code"
          inputMode={useRecovery ? 'text' : 'numeric'}
          maxLength={useRecovery ? 14 : 6}
          value={code}
          onChange={(e) =>
            setCode(
              useRecovery
                ? e.target.value.toUpperCase()
                : e.target.value.replace(/\D/g, ''),
            )
          }
        />
      </Field>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        block
        loading={busy}
        disabled={!ready}
      >
        Continue
      </Button>
      {!setupRequired && (
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => {
            setUseRecovery((v) => !v);
            setCode('');
            setError(null);
          }}
        >
          {useRecovery ? 'Use the code from my app' : 'Use a recovery code'}
        </button>
      )}
      <button type="button" className={styles.linkButton} onClick={cancelMfa}>
        Back to sign in
      </button>
    </form>
  );
}

/** Shown once after two-factor was turned on at sign-in. */
export function RecoveryCodesStep() {
  const { recoveryCodes, acknowledgeRecoveryCodes } = useSession();
  return (
    <div className={styles.form}>
      <h2 className={styles.title}>Two-factor is on</h2>
      <RecoveryCodes codes={recoveryCodes ?? []} />
      <Button
        variant="primary"
        size="lg"
        block
        onClick={acknowledgeRecoveryCodes}
      >
        I have saved them
      </Button>
    </div>
  );
}
